/**
 * Anime rail with minimal sub-genre filter pills.
 *
 * Renders the curated ★8+ anime list with a row of compact pills above
 * (All / Action / Drama / Comedy / Romance / Sci-Fi). Pills are derived
 * dynamically from what's actually in the data — if no Romance anime is
 * present, no Romance pill renders. Filtering is client-side (zero extra
 * network calls).
 */
import { useMemo, useState } from "react";
import MovieCarousel from "./MovieCarousel";

// Display label → TMDB `genre_names` value(s). First non-empty match wins.
const SUB_GENRES = [
  { key: "all",     label: "All",     match: null },
  { key: "action",  label: "Action",  match: ["Action & Adventure", "Action"] },
  { key: "drama",   label: "Drama",   match: ["Drama"] },
  { key: "comedy",  label: "Comedy",  match: ["Comedy"] },
  { key: "romance", label: "Romance", match: ["Romance"] },
  { key: "scifi",   label: "Sci-Fi",  match: ["Sci-Fi & Fantasy", "Science Fiction"] },
  { key: "mystery", label: "Mystery", match: ["Mystery"] },
];

export default function HomeAnimeRail({ items }) {
  const [selected, setSelected] = useState("all");

  // Build the pill list — drop genres that produce zero matches in the
  // current dataset so the bar never shows an empty filter.
  const availablePills = useMemo(() => {
    const counts = {};
    for (const it of items || []) {
      const names = it.genre_names || [];
      for (const sg of SUB_GENRES) {
        if (!sg.match) continue;
        if (sg.match.some((m) => names.includes(m))) {
          counts[sg.key] = (counts[sg.key] || 0) + 1;
        }
      }
    }
    // "all" always shown first; others only if they have ≥3 items (avoid
    // single-card filters that feel empty).
    return SUB_GENRES.filter((sg) => sg.key === "all" || (counts[sg.key] || 0) >= 3);
  }, [items]);

  const filtered = useMemo(() => {
    if (selected === "all") return items;
    const sg = SUB_GENRES.find((s) => s.key === selected);
    if (!sg || !sg.match) return items;
    return (items || []).filter((it) =>
      sg.match.some((m) => (it.genre_names || []).includes(m))
    );
  }, [items, selected]);

  if (!items || items.length === 0) return null;

  return (
    <section data-testid="home-anime-rail-section">
      {/* Pill bar — sits ABOVE MovieCarousel so it shares the same horizontal
          padding/max-width container. Compact, scrolls horizontally on
          narrow phones if needed. */}
      <div className="px-6 lg:px-10 max-w-[1440px] mx-auto mt-12 mb-1">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="anime-subgenre-pills">
          {availablePills.map((sg) => {
            const active = sg.key === selected;
            return (
              <button
                key={sg.key}
                type="button"
                onClick={() => setSelected(sg.key)}
                aria-pressed={active}
                data-testid={`anime-subgenre-${sg.key}`}
                className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border whitespace-nowrap transition-colors ${
                  active
                    ? "bg-pink-500/20 text-pink-200 border-pink-500/50"
                    : "bg-white/[0.04] text-white/55 border-white/10 hover:text-white"
                }`}
              >
                {sg.label}
              </button>
            );
          })}
        </div>
      </div>
      <MovieCarousel
        title="Anime"
        items={filtered}
        testid="row-anime-rail"
        viewAllHref="/list/anime-rail"
        showPlatform
      />
    </section>
  );
}
